import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as roomTypesApi from './room-types.api';

import type {
  CreateRoomTypeRequest,
  RoomTypeListQuery,
  UpdateRoomTypeRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: RoomTypeListQuery) => ['room-types', 'list', query] as const,
  detail: (id: string) => ['room-types', 'detail', id] as const,
};

export const useRoomTypesList = (query: RoomTypeListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => roomTypesApi.listRoomTypes(query),
    staleTime: 30_000,
  });

export const useCreateRoomType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRoomTypeRequest) => roomTypesApi.createRoomType(payload),
    onSuccess: (rt) => {
      qc.invalidateQueries({ queryKey: ['room-types', 'list'] });
      toast.success(`Added ${rt.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add room type'),
  });
};

export const useUpdateRoomType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRoomTypeRequest }) =>
      roomTypesApi.updateRoomType(id, payload),
    onSuccess: (rt) => {
      qc.invalidateQueries({ queryKey: ['room-types', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(rt.id) });
      toast.success('Room type updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update room type'),
  });
};

export const useDeleteRoomType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => roomTypesApi.deleteRoomType(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['room-types', 'list'] });
      toast.success('Room type deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete room type'),
  });
};

export const useBulkDeleteRoomTypes = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => roomTypesApi.bulkDeleteRoomTypes(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['room-types'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete room types'),
  });
};

export const useBulkSetRoomTypesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      roomTypesApi.bulkSetRoomTypesStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['room-types'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update room type status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
